/**
 * QuickLogForm (PH4-8) — pure form component.
 *
 * Extracted from QuickLogPage so it can be rendered both as a standalone
 * route (legacy /quick-log) and inside a BottomSheet (QuickLogSheet).
 */
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, CircleNotch } from "@phosphor-icons/react";

import { EmptyState } from "../EmptyState";
import { InlineError } from "../InlineError";
import { api } from "../../api";
import { toLocalISODate, todayLocalISO } from "../../lib/localDate";

const TYPE_KEYS = ["run", "trail", "ride", "swim", "strength", "other"] as const;
type TypeKey = (typeof TYPE_KEYS)[number];

export interface QuickLogFormProps {
  /** Optional callback fired after a successful save. Sheet uses it to close. */
  onSaved?: () => void;
  /** When true, hides the desktop ⌘+Enter hint (the sheet has its own footer). */
  hideShortcutHint?: boolean;
}

export function QuickLogForm({ onSaved, hideShortcutHint = false }: QuickLogFormProps) {
  const { t } = useTranslation("quick_log");
  const [type, setType] = useState<TypeKey>("run");
  // Бэкфил: тренировка без гаджета чаще вносится назавтра. Поле date в API
  // было всегда — UI просто не давал им пользоваться. Окно = 90 дней, как
  // у wellness-ввода.
  const [date, setDate] = useState(todayLocalISO());
  const [duration, setDuration] = useState("");
  const [distance, setDistance] = useState("");
  const [rpe, setRpe] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async () => {
    if (!duration) return;
    setSaving(true);
    setError(null);
    try {
      await api.post("/athlete/quick-log", {
        discipline: type,
        duration_minutes: parseInt(duration, 10),
        distance_km: distance ? parseFloat(distance) : null,
        rpe,
        notes: notes || null,
        // The athlete's calendar day is only known on the device: the server
        // falls back to the UTC day, which is wrong for two hours around
        // midnight in CET/CEST.
        date,
      });
      setSaved(true);
      if (onSaved) {
        setTimeout(() => onSaved(), 1200);
      }
    } catch (e) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || t("error_msg"));
    } finally {
      setSaving(false);
    }
  }, [duration, distance, type, rpe, notes, date, onSaved, t]);

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && duration && !saving) {
      e.preventDefault();
      void submit();
    }
  };

  if (saved) {
    return (
      <EmptyState
        testId="quick-log-saved"
        icon={CheckCircle}
        title={t("saved_title")}
        description={t("saved_msg")}
      />
    );
  }

  return (
    <div className="space-y-5" onKeyDown={onKeyDown}>
      <p className="text-xs text-fg-muted">{t("intro")}</p>

      {error && (
        <InlineError
          testId="quick-log-error"
          title={t("error_title")}
          message={error}
          onRetry={submit}
          retryLabel={t("retry")}
        />
      )}

      <div className="space-y-2">
        <span id="quick-log-type-label" className="text-sm font-medium text-fg-secondary">
          {t("type_label")}
        </span>
        <div
          role="radiogroup"
          aria-labelledby="quick-log-type-label"
          className="flex flex-wrap gap-2"
        >
          {TYPE_KEYS.map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={type === k}
              onClick={() => setType(k)}
              data-testid={`quick-log-type-${k}`}
              className={`px-3 py-1.5 rounded-lg text-sm border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${
                type === k
                  ? "bg-accent/10 border-accent/40 text-accent"
                  : "bg-bg-surface border-bg-border text-fg-secondary"
              }`}
            >
              {t(`type_${k}`)}
            </button>
          ))}
        </div>
        {/* W-FIX-B: специализированные журналы (/log/strength, /log/swim-drill,
            /workout/brick) были orphan-роутами — контекстный вход отсюда */}
        {type === "strength" && (
          <Link
            to="/log/strength"
            data-testid="quick-log-strength-link"
            className="inline-flex items-center gap-1 text-[11px] text-accent font-medium hover:underline"
          >
            {t("detailed_strength_link", { defaultValue: "Подробный силовой журнал (упражнения, подходы)" })} →
          </Link>
        )}
        {type === "swim" && (
          <Link
            to="/log/swim-drill"
            data-testid="quick-log-swim-drill-link"
            className="inline-flex items-center gap-1 text-[11px] text-accent font-medium hover:underline"
          >
            {t("swim_drill_link", { defaultValue: "Журнал плавательных драйлов" })} →
          </Link>
        )}
        {type === "other" && (
          <Link
            to="/workout/brick"
            data-testid="quick-log-brick-link"
            className="inline-flex items-center gap-1 text-[11px] text-accent font-medium hover:underline"
          >
            {t("brick_link", { defaultValue: "Brick-сессия (вело + бег)" })} →
          </Link>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="quick-log-date" className="text-sm font-medium text-fg-secondary">
          {t("date_label", { defaultValue: "Когда" })}
        </label>
        <input
          id="quick-log-date"
          type="date"
          value={date}
          max={todayLocalISO()}
          min={toLocalISODate(new Date(Date.now() - 90 * 86400e3))}
          onChange={(e) => setDate(e.target.value || todayLocalISO())}
          data-testid="quick-log-date"
          className="w-full bg-bg-surface border border-bg-border rounded-tile px-4 py-3 text-sm text-fg-primary placeholder-fg-muted focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="quick-log-duration" className="text-sm font-medium text-fg-secondary">
          {t("duration_label")}
        </label>
        <input
          id="quick-log-duration"
          type="number"
          inputMode="numeric"
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          placeholder={t("duration_placeholder")}
          required
          data-testid="quick-log-duration"
          className="w-full bg-bg-surface border border-bg-border rounded-tile px-4 py-3 text-sm text-fg-primary placeholder-fg-muted focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="quick-log-distance" className="text-sm font-medium text-fg-secondary">
          {t("distance_label")}
        </label>
        <input
          id="quick-log-distance"
          type="number"
          inputMode="decimal"
          step="0.1"
          value={distance}
          onChange={(e) => setDistance(e.target.value)}
          placeholder={t("distance_placeholder")}
          className="w-full bg-bg-surface border border-bg-border rounded-tile px-4 py-3 text-sm text-fg-primary placeholder-fg-muted focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
        />
      </div>

      <div className="space-y-2">
        <span id="quick-log-rpe-label" className="text-sm font-medium text-fg-secondary">
          {t("rpe_label")}
        </span>
        <div
          role="radiogroup"
          aria-labelledby="quick-log-rpe-label"
          className="flex gap-1.5"
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((v) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={rpe === v}
              aria-label={t("rpe_aria", { value: v })}
              onClick={() => setRpe(rpe === v ? null : v)}
              data-testid={`quick-log-rpe-${v}`}
              className={`flex-1 py-2 rounded text-xs font-medium border transition-colors num-display focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${
                rpe === v
                  ? "bg-accent text-white border-accent"
                  : "bg-bg-surface border-bg-border text-fg-muted"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="quick-log-notes" className="text-sm font-medium text-fg-secondary">
          {t("notes_label")}
        </label>
        <textarea
          id="quick-log-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t("notes_placeholder")}
          rows={3}
          data-testid="quick-log-notes"
          className="w-full bg-bg-surface border border-bg-border rounded-tile px-4 py-3 text-sm text-fg-primary placeholder-fg-muted focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 resize-none"
        />
      </div>

      <div className="space-y-2">
        <button
          type="button"
          onClick={submit}
          disabled={saving || !duration}
          data-testid="quick-log-submit"
          className="w-full py-3 bg-accent text-white font-semibold rounded-tile disabled:opacity-50 inline-flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
        >
          {saving && <CircleNotch className="w-4 h-4 animate-spin" aria-hidden="true" />}
          {saving ? t("submitting") : t("submit")}
        </button>
        {!hideShortcutHint && (
          <div className="text-[10px] text-fg-dim text-center hidden lg:block">
            {t("shortcut_hint")}
          </div>
        )}
      </div>
    </div>
  );
}
